import React from 'react'
import { Head, router } from '@inertiajs/react'
import AdminLayout from '../../../components/AdminLayout'
import AdminTable from '../../../components/AdminTable'

interface User {
  id: number
  email: string
  name: string
  role: string
  status: string
  created_at: string
  is_current_user: boolean
}

interface Props {
  users: User[]
  pagination: {
    current_page: number
    total_pages: number
    total_count: number
    per_page: number
    prev_page?: number
    next_page?: number
  }
  filters: {
    search?: string
    role?: string
  }
  filter_options: {
    roles: string[]
  }
  can_edit: boolean
  current_user: {
    id: number
    email: string
    role: string
  }
}

function UsersIndex({ users, pagination, filters, filter_options, can_edit, current_user }: Props) {
  const columns = [
    { key: 'email', label: 'Email' },
    { key: 'name', label: 'Name' },
    { key: 'role', label: 'Role' },
    { key: 'status', label: 'Status' },
    { key: 'created_at', label: 'Created At' },
  ]

  const formFields = [
    { name: 'email', label: 'Email', type: 'email' as const, required: true },
    { name: 'name', label: 'Name', type: 'text' as const, required: true },
    { 
      name: 'role', 
      label: 'Role', 
      type: 'select' as const, 
      required: true,
      options: filter_options.roles.map(role => ({ value: role, label: role.charAt(0).toUpperCase() + role.slice(1) }))
    }
  ]

  const handleCreate = (formData: any) => {
    router.post('/users/invite', formData)
  }

  const handleEdit = (item: any) => {
    if (item.is_current_user) {
      alert('You cannot edit your own user record.')
      return
    }
    
    // Determine what's being updated
    const updates = []
    if (item.name !== undefined) updates.push('name')
    if (item.role !== undefined) updates.push('role')
    
    const updateText = updates.join(' and ')
    const isConfirmed = window.confirm(`Are you sure you want to update the ${updateText} for "${item.email}"?`)
    
    if (isConfirmed) {
      const params: any = {}
      if (item.name !== undefined) params.name = item.name
      if (item.role !== undefined) params.role = item.role
      
      router.patch(`/users/${item.id}/update_role`, params)
    }
  }

  const handleDelete = (item: any) => {
    if (item.is_current_user) {
      alert('You cannot delete your own account.')
      return
    }
    
    const isConfirmed = window.confirm(
      `Are you sure you want to delete the user "${item.email}"?\n\nThis action cannot be undone.`
    )
    if (isConfirmed) {
      router.delete(`/users/${item.id}`)
    }
  }

  return (
    <>
      <Head title="Users | Mailkit - Resolute" />
      <AdminTable
        title="Users"
        description="Manage users and permissions in the system"
        data={users}
        columns={columns}
        pagination={pagination}
        filters={filters}
        filter_options={filter_options}
        can_edit={can_edit}
        onCreate={handleCreate}
        onEdit={handleEdit}
        onDelete={handleDelete}
        searchPlaceholder="Search users..."
        basePath="/users"
        formFields={formFields}
      />
    </>
  )
}

UsersIndex.layout = page => <AdminLayout>{page}</AdminLayout>

export default UsersIndex